import React, { useState } from "react";
import { DialogLayout } from "../../../../ui";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { FeatherRefreshCw, FeatherSettings } from "@subframe/core";
import { TdsCurationList } from "./TdsCurationCard";
import {
  previewTdsSync,
  applyTdsSync,
  TdsSyncPreviewResult,
  TdsSyncApplyResult,
  TdsSyncPreviewRow,
  TdsCurationReason,
} from "../../../../services/api";

const FIELD_LABELS: Record<string, string> = {
  tds_tax_name: "Name",
  tds_tax_percentage: "Rate %",
  tds_tax_type: "Type",
  tax_specific_type: "Section",
  is_expired: "Expired",
};

/** The curated columns a sync can fill in, as the modal names them. */
const FILL_LABELS: Record<string, string> = {
  section: "Section",
  llm_context: "AI context",
};

/**
 * What the sync is about to write into a row's blank curation, one line each.
 *
 * The two display flags always move together (a bare row gets both), so they
 * read as one fact rather than two rows of `business_display: (blank) → 1`.
 */
const fillLines = (fill: Record<string, string | number>): string[] => {
  const lines: string[] = [];

  Object.entries(fill).forEach(([field, value]) => {
    if (field === "business_display" || field === "individual_display") return;
    lines.push(`${FILL_LABELS[field] || field}: (blank) → ${value}`);
  });

  if (
    fill.business_display !== undefined ||
    fill.individual_display !== undefined
  ) {
    lines.push("Picker visibility: hidden → shown");
  }

  return lines;
};

/**
 * What is left for a human once the sync has seeded what the catalogue knows
 * (ZOUR-55). Only these rows are worth naming — everything else is finished.
 */
const CURATION_REASON_COPY: Record<string, string> = {
  unknown_section:
    "section is not in the catalogue, so it has to be typed in by hand",
  shared_section:
    "shares its section with another active rate, so the AI context has to be written by hand to tell them apart",
  needs_context: "no AI context yet, so nothing will suggest it",
};

const CURATION_REASONS = [
  "unknown_section",
  "shared_section",
  "needs_context",
] as const;

/**
 * The rates the catalogue could not finish, grouped by what is missing.
 *
 * Shown in the preview as well as after the apply: a rate whose only gap is a
 * shared section leaves nothing to save, so the preview would otherwise read
 * "Everything up to date" over a rate nothing will ever suggest.
 *
 * The lead names no destination: this card is mounted on the admin Sync tab,
 * where curation is behind its own Manage button, AND on /settings/tds-tcs,
 * where the same fields are behind a row's "..." → Edit. It also does not name
 * WHICH field is missing — that is per row, in CURATION_REASON_COPY.
 */
const curationLead = (count: number): string =>
  `${count} rate(s) still need a hand before anything suggests them — open the rate to finish it:`;

const curationReasonLines = (rows: TdsSyncPreviewRow[]): React.ReactNode[] =>
  CURATION_REASONS.flatMap((reason) => {
    const matching = rows.filter(
      (r) => r.curation_reason === (reason as TdsCurationReason),
    );
    if (matching.length === 0) return [];
    return [
      <span key={reason} className="text-amber-800">
        {matching
          .map((r) => `${r.tds_tax_name} (${r.tds_tax_percentage}%)`)
          .join(", ")}{" "}
        — {CURATION_REASON_COPY[reason]}
      </span>,
    ];
  });

const formatChangeValue = (field: string, value: string | number): string => {
  if (field === "is_expired") {
    return Number(value) === 1 ? "Yes" : "No";
  }
  const text = String(value);
  return text === "" ? "(blank)" : text;
};

const statusBadge = (row: TdsSyncPreviewRow): React.ReactNode =>
  row.is_expired === 1 ? (
    <Badge variant="neutral">Expired</Badge>
  ) : (
    <Badge variant="success">Active</Badge>
  );

export interface TdsSyncCardProps {
  /**
   * The bordered card and its "Sync TDS Taxes" heading. Off when the host
   * already supplies both, as the settings panel does — otherwise the page shows
   * a heading inside a heading.
   */
  showHeader?: boolean;
  /**
   * The "Manage" button, which opens the curation list in a modal. Off where the
   * list is already on the page: a modal over a copy of what is behind it.
   */
  showManageAction?: boolean;
  /** Fired after a sync is applied, so a sibling rate list can refresh itself. */
  onApplied?: () => void;
}

/**
 * "Sync TDS Taxes" card for the admin Sync tab, and the sync action on
 * /settings/tds-tcs.
 *
 * Click fetches a read-only preview of Zoho's TDS tax list diffed against
 * tds_rates and shows it in a modal (NEW / CHANGED / SAME + local-only rows).
 * Saving calls the apply endpoint, which re-fetches from Zoho server-side and
 * upserts the Zoho-owned fields, seeding a blank section or AI context from the
 * tds_section_types catalogue as it goes (ZOUR-55) — a curated value someone has
 * typed is never touched, and local-only rows are never deleted.
 */
export const TdsSyncCard: React.FC<TdsSyncCardProps> = ({
  showHeader = true,
  showManageAction = true,
  onApplied,
}) => {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<TdsSyncPreviewResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<TdsSyncApplyResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    setApplyResult(null);
    setPreview(null);

    try {
      const result = await previewTdsSync();
      setPreview(result);
      setModalOpen(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to preview TDS sync",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError(null);

    try {
      const result = await applyTdsSync();
      setApplyResult(result);
      // The rate list is a sibling on /settings/tds-tcs, not a child of this
      // card, so it cannot see the sync landing. Without this it keeps showing
      // pre-sync rows until someone presses Reload.
      onApplied?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply TDS sync");
    } finally {
      setApplying(false);
    }
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setModalOpen(false);
      setApplyResult(null);
    }
  };

  const counts = preview?.data.counts;
  // A row Zoho has not moved but whose section is blank is still work: without
  // it in the total the button reads "Everything up to date" over six rates the
  // sync is about to label (ZOUR-55).
  const pendingChanges = counts
    ? counts.new + counts.changed + counts.labelled
    : 0;

  const renderRow = (
    row: TdsSyncPreviewRow,
    kind: "new" | "changed" | "same",
  ): React.ReactNode => (
    <tr
      key={row.tds_tax_id}
      className="border-b border-solid border-neutral-border last:border-b-0"
    >
      <td className="px-3 py-2 align-top">
        {kind === "new" && <Badge variant="brand">New</Badge>}
        {kind === "changed" && <Badge variant="warning">Changed</Badge>}
        {kind === "same" && <Badge variant="neutral">Same</Badge>}
      </td>
      <td className="px-3 py-2 align-top">
        <span className="text-body font-body text-default-font">
          {row.tds_tax_name}
        </span>
        {kind === "changed" && row.changes && (
          <div className="mt-1 flex flex-col gap-0.5">
            {Object.entries(row.changes).map(([field, change]) => (
              <span
                key={field}
                className="text-caption font-caption text-amber-700"
              >
                {FIELD_LABELS[field] || field}:{" "}
                {formatChangeValue(field, change.old)} →{" "}
                {formatChangeValue(field, change.new)}
              </span>
            ))}
          </div>
        )}
        {row.label_fill && Object.keys(row.label_fill).length > 0 && (
          <div className="mt-1 flex flex-col gap-0.5">
            {fillLines(row.label_fill).map((line) => (
              <span
                key={line}
                className="text-caption font-caption text-green-700"
              >
                {line}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top text-body font-body text-default-font whitespace-nowrap">
        {row.tds_tax_percentage}%
      </td>
      <td className="px-3 py-2 align-top text-body font-body text-subtext-color">
        {/* What will be STORED, not Zoho's slug: a new row arrives with the
            catalogue label already derived, so the operator sees the value the
            insert will write. The slug only shows when the catalogue cannot
            place it — which is exactly the row that needs a human. */}
        {row.section || row.tax_specific_type || "—"}
      </td>
      <td className="px-3 py-2 align-top">{statusBadge(row)}</td>
    </tr>
  );

  return (
    <div
      className={
        showHeader
          ? "flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6"
          : // Not w-full: without its own heading this is just a button, and a
            // full-width wrapper would force it onto a line of its own instead of
            // sitting beside the host's heading.
            "flex flex-col items-start gap-4"
      }
    >
      <div
        className={`flex items-center ${
          showHeader ? "w-full justify-between" : "justify-end"
        }`}
      >
        {showHeader && (
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Sync TDS Taxes
            </span>
            <span className="text-body font-body text-subtext-color">
              Preview TDS rates from Zoho Books, then confirm to save.
            </span>
          </div>
        )}
        <div className="flex flex-shrink-0 items-center gap-2">
          {showManageAction && (
            <Button
              variant="neutral-secondary"
              icon={<FeatherSettings />}
              onClick={() => setManageOpen(true)}
            >
              Manage
            </Button>
          )}
          <Button
            variant="brand-primary"
            icon={<FeatherRefreshCw />}
            onClick={handlePreview}
            disabled={previewing}
          >
            {previewing ? "Fetching..." : "Sync TDS"}
          </Button>
        </div>
      </div>

      {error && !modalOpen && (
        <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      <DialogLayout open={modalOpen} onOpenChange={handleModalOpenChange}>
        <div className="flex w-192 max-w-full flex-col items-start gap-4 bg-default-background px-8 py-6">
          <span className="text-heading-2 font-heading-2 text-default-font">
            TDS Taxes from Zoho ({preview?.data.total_from_zoho ?? 0})
          </span>

          {counts && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="brand">{counts.new} new</Badge>
              <Badge variant="warning">{counts.changed} changed</Badge>
              <Badge variant="neutral">{counts.same} unchanged</Badge>
              {counts.labelled > 0 && (
                <Badge variant="success">{counts.labelled} to label</Badge>
              )}
              {counts.orphaned > 0 && (
                <Badge variant="error">{counts.orphaned} local-only</Badge>
              )}
            </div>
          )}

          {error && (
            <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
              <span className="text-body font-body text-red-600">{error}</span>
            </div>
          )}

          {applyResult ? (
            <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
              <span className="text-body font-body font-semibold text-green-800">
                TDS Sync Completed
              </span>
              <div className="flex w-full flex-col items-start gap-1 text-sm text-green-700">
                <span>
                  {applyResult.data.new_added} added ·{" "}
                  {applyResult.data.updated} updated ·{" "}
                  {applyResult.data.unchanged} unchanged
                </span>
                {applyResult.data.labelled > 0 && (
                  <span>
                    {applyResult.data.labelled} rate(s) had a blank section or
                    AI context filled in from the section catalogue.
                  </span>
                )}
                {applyResult.data.needs_curation.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-1">
                    <span className="text-amber-800">
                      {curationLead(applyResult.data.needs_curation.length)}
                    </span>
                    {curationReasonLines(applyResult.data.needs_curation)}
                  </div>
                )}
                {applyResult.data.errors > 0 && (
                  <div className="flex w-full flex-col items-start gap-1">
                    <span className="font-semibold text-red-600">
                      Errors: {applyResult.data.errors}
                    </span>
                    <ul className="list-disc list-inside text-xs">
                      {applyResult.data.error_details.map((detail, index) => (
                        <li key={index} className="text-red-600">
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : (
            preview && (
              <div className="flex w-full flex-col items-start gap-3">
                <div className="w-full max-h-96 overflow-auto rounded-md border border-solid border-neutral-border">
                  <table className="w-full border-collapse text-left">
                    <thead className="sticky top-0 bg-neutral-50">
                      <tr className="border-b border-solid border-neutral-border">
                        <th className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color">
                          Status
                        </th>
                        <th className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color">
                          Name
                        </th>
                        <th className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color">
                          Rate
                        </th>
                        <th className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color">
                          Section
                        </th>
                        <th className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color">
                          Zoho Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        [
                          {
                            title: "Active — FY 2026-27 (§393)",
                            expired: 0,
                          },
                          {
                            title: "Expired — pre FY 2026-27",
                            expired: 1,
                          },
                        ] as const
                      ).map((group) => {
                        const inGroup = (row: TdsSyncPreviewRow) =>
                          row.is_expired === group.expired;
                        const newRows = preview.data.new.filter(inGroup);
                        const changedRows =
                          preview.data.changed.filter(inGroup);
                        const sameRows = preview.data.same.filter(inGroup);
                        const total =
                          newRows.length + changedRows.length + sameRows.length;
                        if (total === 0) return null;
                        return (
                          <React.Fragment key={group.title}>
                            <tr>
                              <td
                                colSpan={5}
                                className="border-b border-solid border-neutral-border bg-neutral-100 px-3 py-1.5 text-caption-bold font-caption-bold text-subtext-color"
                              >
                                {group.title} ({total})
                              </td>
                            </tr>
                            {newRows.map((row) => renderRow(row, "new"))}
                            {changedRows.map((row) =>
                              renderRow(row, "changed"),
                            )}
                            {sameRows.map((row) => renderRow(row, "same"))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {preview.data.needs_curation.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-amber-200 bg-amber-50 p-3">
                    <span className="text-body font-body font-semibold text-amber-800">
                      {curationLead(preview.data.needs_curation.length)}
                    </span>
                    <div className="flex w-full flex-col items-start gap-0.5 text-sm">
                      {curationReasonLines(preview.data.needs_curation)}
                    </div>
                  </div>
                )}

                {preview.data.orphaned.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-amber-200 bg-amber-50 p-3">
                    <span className="text-body font-body font-semibold text-amber-800">
                      {preview.data.orphaned.length} local rate(s) not found in
                      Zoho (won't be touched):
                    </span>
                    <ul className="list-disc list-inside text-sm text-amber-800">
                      {preview.data.orphaned.map((row) => (
                        <li key={row.tds_tax_id}>
                          {row.tds_tax_name} ({row.tds_tax_percentage}%) — ID{" "}
                          {row.tds_tax_id}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <span className="text-caption font-caption text-subtext-color">
                  Saving updates names, rates and expiry from Zoho, and fills a
                  blank section or AI context from the section catalogue. Nothing
                  you have typed is overwritten. A rate the catalogue cannot
                  finish is listed above, for you to complete on the rate itself.
                </span>
              </div>
            )
          )}

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-secondary"
              onClick={() => handleModalOpenChange(false)}
              disabled={applying}
            >
              {applyResult ? "Close" : "Cancel"}
            </Button>
            {!applyResult && (
              <Button
                variant="brand-primary"
                onClick={handleApply}
                disabled={applying || pendingChanges === 0}
              >
                {applying
                  ? "Saving..."
                  : pendingChanges === 0
                    ? "Everything up to date"
                    : `Save ${pendingChanges} change${pendingChanges === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
        </div>
      </DialogLayout>

      <DialogLayout open={manageOpen} onOpenChange={setManageOpen}>
        <div className="flex w-256 max-w-full flex-col items-start gap-4 bg-default-background px-8 py-6">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Manage TDS Rates
            </span>
            <span className="text-body font-body text-subtext-color">
              Set the section label, picker visibility and AI context for each
              rate. Names, rates and expiry come from Zoho via Sync TDS.
            </span>
          </div>

          <TdsCurationList />

          <div className="flex w-full items-center justify-end">
            <Button
              variant="neutral-secondary"
              onClick={() => setManageOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};

export default TdsSyncCard;
